import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import { 
  FaPen, FaPlus, FaFilter, FaDownload, FaSearch, FaTimes, FaEye, 
  FaImage, FaTag, FaPercentage, FaUsers, FaChevronLeft, FaChevronRight,
  FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimesCircle,
  FaArrowLeft, FaArrowRight, FaBars, FaThLarge, FaList, FaSort,
  FaSortUp, FaSortDown, FaEdit, FaTrash, FaCopy, FaBuilding
} from 'react-icons/fa';
import Select from 'react-select';

// Make offer type options smaller
const OFFER_TYPE_OPTIONS = [
  { value: 'spl', label: 'SPL' },
  { value: 'hvr', label: 'HVR' },
  { value: 'nrp', label: 'NRP' },
  { value: 'flat', label: 'FLAT' },
  { value: 'clp', label: 'CLP' },
  { value: 'kap', label: 'KAP' },
  { value: 'sdp', label: 'SDP' }
];

const BrandManagement = () => {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const { user } = useContext(AuthContext);

  // Pagination and filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBrands, setTotalBrands] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [nameFilterTimeout, setNameFilterTimeout] = useState(null);
  
  // UI state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [offerTypeFilter, setOfferTypeFilter] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    image: null,
    categories: [],
    supportedOfferTypes: [],
    salespersonBasicIncentive: 0,
    referenceBasicIncentive: 0
  });

  // Format categories for react-select
  const categoryOptions = categories.map(category => ({
    value: category._id,
    label: category.name
  }));

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);
  
  // Separate useEffect for fetching brands to avoid unnecessary category fetches
  useEffect(() => {
    fetchBrands();
  }, [page, limit, nameFilter]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      Swal.fire('Error', 'Failed to fetch categories', 'error');
    }
  };

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Build query string for pagination and search
      const queryParams = new URLSearchParams({
        page,
        limit
      });
      
      // Only add name filter if it's not empty
      if (nameFilter && nameFilter.trim() !== '') {
        queryParams.set('name', nameFilter.trim());
        console.log('Adding name filter:', nameFilter.trim());
      }
      
      const queryString = queryParams.toString();
      console.log('API query string:', queryString);
      
      const response = await api.get(`/brands/paginated?${queryString}`);
      
      console.log('Paginated brands response:', response.data);
      
      // Handle the response data
      if (response.data && Array.isArray(response.data.brands)) {
        setBrands(response.data.brands);
        setTotalPages(response.data.totalPages || 1);
        setTotalBrands(response.data.totalBrands || 0);
        
        console.log('Set brands count:', response.data.brands.length);
        console.log('Total brands:', response.data.totalBrands);
        console.log('Total pages:', response.data.totalPages);
      } else {
        console.warn('Unexpected response format:', response.data);
        setBrands([]);
        setTotalPages(1);
        setTotalBrands(0);
      }
    } catch (error) {
      console.error('Error fetching brands:', error.response || error);
      setBrands([]);
      setTotalPages(1);
      setTotalBrands(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle name filter change with debouncing
  const handleNameFilterChange = (e) => {
    const value = e.target.value;
    setNameFilter(value);
    
    // Clear any existing timeout
    if (nameFilterTimeout) {
      clearTimeout(nameFilterTimeout);
    }
    
    // Set a new timeout to delay the API call
    const timeoutId = setTimeout(() => {
      setPage(1); // Reset to first page when search changes
      // fetchBrands() will be called via useEffect when nameFilter changes
    }, 300); // 300ms delay
    
    setNameFilterTimeout(timeoutId);
  };

  // Handle pagination
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCategoryChange = (selectedOptions) => {
    setFormData({
      ...formData,
      categories: selectedOptions ? selectedOptions.map(option => option.value) : []
    });
  };

  const handleOfferTypesChange = (selectedOptions) => {
    setFormData({
      ...formData,
      supportedOfferTypes: selectedOptions ? selectedOptions.map(option => option.value) : []
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image: null,
      categories: [],
      supportedOfferTypes: [],
      salespersonBasicIncentive: 0,
      referenceBasicIncentive: 0
    });
    setImagePreview(null);
    setCurrentBrand(null);
    setIsEditing(false);
  };

  const handleDeleteBrand = async (brand) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the brand "${brand.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/brands/${brand._id}`);
        Swal.fire('Deleted!', `Brand "${brand.name}" has been deleted.`, 'success');
        fetchBrands();
      } catch (error) {
        console.error('Error deleting brand:', error);
        Swal.fire('Error', 'Failed to delete brand. Please try again.', 'error');
      }
    }
  };

  const openModal = (brand = null) => {
    if (brand) {
      setFormData({
        name: brand.name,
        image: null,
        categories: brand.categories?.map(cat => typeof cat === 'object' ? cat._id : cat) || [],
        supportedOfferTypes: brand.supportedOfferTypes || [],
        salespersonBasicIncentive: brand.salespersonBasicIncentive || 0,
        referenceBasicIncentive: brand.referenceBasicIncentive || brand.architectInteriorDesignerBasicIncentive || brand.architectInteriorBasicIncentive || 0
      });
      setCurrentBrand(brand);
      setIsEditing(true);
      if (brand._id) {
        setImagePreview(`${process.env.REACT_APP_API_BASE_URL}/brands/image/${brand._id}?t=${Date.now()}`);
      }
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      Swal.fire('Error', 'Brand name is required', 'error');
      return;
    }

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('salespersonBasicIncentive', formData.salespersonBasicIncentive);
    submitData.append('referenceBasicIncentive', formData.referenceBasicIncentive);
    
    // Handle categories - ensure array is properly sent
    if (formData.categories && formData.categories.length > 0) {
      formData.categories.forEach(categoryId => {
        submitData.append('categories[]', categoryId);
      });
    } else {
      // Send empty array indicator
      submitData.append('categories', JSON.stringify([]));
    }
    
    // Handle supportedOfferTypes - ensure array is properly sent
    if (formData.supportedOfferTypes && formData.supportedOfferTypes.length > 0) {
      formData.supportedOfferTypes.forEach(offerType => {
        submitData.append('supportedOfferTypes[]', offerType);
      });
    } else {
      // Send empty array indicator
      submitData.append('supportedOfferTypes', JSON.stringify([]));
    }
    
    if (formData.image) {
      submitData.append('image', formData.image);
    }

    // Debug: Log what we're sending
    console.log('Form data being sent:');
    console.log('- name:', formData.name);
    console.log('- categories:', formData.categories);
    console.log('- supportedOfferTypes:', formData.supportedOfferTypes);
    console.log('- image:', formData.image ? 'Present' : 'Not present');

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 
          'Authorization': `Bearer ${token}`,
        }
      });

      if (isEditing && currentBrand) {
        await api.put(`/brands/${currentBrand._id}`, submitData);
        Swal.fire('Success', 'Brand updated successfully', 'success');
      } else {
        await api.post('/brands', submitData);
        Swal.fire('Success', 'Brand created successfully', 'success');
      }

      closeModal();
      fetchBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save brand';
      Swal.fire('Error', errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setNameFilter('');
    setPage(1);
  };

  // Export brands function
  const exportBrands = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const response = await api.get('/brands/export', {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `brands_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire('Success', 'Brands exported successfully!', 'success');
    } catch (error) {
      console.error('Error exporting brands:', error);
      Swal.fire('Error', 'Failed to export brands', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Enhanced Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <FaBuilding className="text-white text-lg" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Brand Management</h1>
                  <p className="text-sm text-gray-500">Manage your brand portfolio</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Stats */}
              <div className="hidden md:flex items-center space-x-4 mr-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">{totalBrands}</div>
                  <div className="text-xs text-gray-500">Total Brands</div>
                </div>
                <div className="w-px h-8 bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">{categories.length}</div>
                  <div className="text-xs text-gray-500">Categories</div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <button
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 shadow-sm"
                onClick={exportBrands}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <FaSpinner className="animate-spin mr-2" size={14} />
                ) : (
                  <FaDownload className="mr-2" size={14} />
                )}
                Export
              </button>
              
              <button
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
                onClick={() => openModal()}
              >
                <FaPlus className="mr-2" size={14} />
                New Brand
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Enhanced Filter Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FaFilter className="mr-2" size={14} />
                  Filters
                  {showFilters && <FaTimes className="ml-2" size={12} />}
                </button>
                
                {/* View Mode Toggle */}
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-2 text-sm font-medium ${
                      viewMode === 'grid'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaThLarge size={14} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-2 text-sm font-medium ${
                      viewMode === 'list'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaList size={14} />
                  </button>
                </div>
              </div>
              
              {/* Search Bar */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search brands..."
                    value={nameFilter}
                    onChange={handleNameFilterChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {nameFilter && (
                    <button
                      onClick={() => setNameFilter('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Expanded Filters */}
            {showFilters && (
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <Select
                      value={categoryFilter ? { value: categoryFilter, label: categories.find(c => c._id === categoryFilter)?.name || '' } : null}
                      onChange={(option) => setCategoryFilter(option?.value || '')}
                      options={[
                        { value: '', label: 'All Categories' },
                        ...categories.map(cat => ({ value: cat._id, label: cat.name }))
                      ]}
                      isClearable
                      placeholder="Select category..."
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Offer Type</label>
                    <Select
                      value={offerTypeFilter ? { value: offerTypeFilter, label: OFFER_TYPE_OPTIONS.find(o => o.value === offerTypeFilter)?.label || '' } : null}
                      onChange={(option) => setOfferTypeFilter(option?.value || '')}
                      options={[
                        { value: '', label: 'All Offer Types' },
                        ...OFFER_TYPE_OPTIONS
                      ]}
                      isClearable
                      placeholder="Select offer type..."
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                    <div className="flex space-x-2">
                      <Select
                        value={{ value: sortBy, label: sortBy.charAt(0).toUpperCase() + sortBy.slice(1) }}
                        onChange={(option) => setSortBy(option.value)}
                        options={[
                          { value: 'name', label: 'Name' },
                          { value: 'createdAt', label: 'Created Date' }
                        ]}
                        className="flex-1 text-sm"
                      />
                      <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Clear Filters */}
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setNameFilter('');
                      setCategoryFilter('');
                      setOfferTypeFilter('');
                      setSortBy('name');
                      setSortOrder('asc');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
            <div className="relative">
              <input
                type="text"
                value={nameFilter}
                onChange={handleNameFilterChange}
                className="w-full p-2 pl-10 border rounded"
                placeholder="Search brands by name..."
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-4">Loading...</div>}

      {!loading && brands.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No brands found. Create a new brand to get started.</p>
        </div>
      )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="flex items-center space-x-3">
              <FaSpinner className="animate-spin text-blue-600" size={24} />
              <span className="text-gray-600">Loading brands...</span>
            </div>
          </div>
        )}

        {/* Brands Display */}
        {!loading && brands.length > 0 && (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {brands.map((brand) => (
                  <div key={brand._id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden">
                    {/* Brand Image */}
                    <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                      <img 
                        src={`${process.env.REACT_APP_API_BASE_URL}/brands/image/${brand._id}?t=${Date.now()}`} 
                        alt={brand.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="text-center" style={{display: 'none'}}>
                        <FaImage className="mx-auto text-gray-400 mb-2" size={32} />
                        <span className="text-gray-500 text-sm">No Image</span>
                      </div>
                      
                      {/* Brand ID Badge */}
                      <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded-md text-xs font-medium">
                        {brand.id || brand._id.toString().substring(0, 8)}
                      </div>
                    </div>
                    
                    {/* Brand Info */}
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{brand.name}</h3>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => openModal(brand)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Brand"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteBrand(brand)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Brand"
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Categories */}
                      {brand.categories && brand.categories.length > 0 && (
                        <div className="mb-3">
                          <div className="flex flex-wrap gap-1">
                            {brand.categories.slice(0, 3).map((category, index) => {
                              const categoryId = category._id || category;
                              const categoryData = categories.find(c => c._id === categoryId);
                              return categoryData ? (
                                <span 
                                  key={index} 
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  <FaTag className="mr-1" size={10} />
                                  {categoryData.name}
                                </span>
                              ) : null;
                            })}
                            {brand.categories.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                +{brand.categories.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Offer Types */}
                      {brand.supportedOfferTypes && brand.supportedOfferTypes.length > 0 && (
                        <div className="mb-3">
                          <div className="flex flex-wrap gap-1">
                            {brand.supportedOfferTypes.slice(0, 3).map((offerType, index) => (
                              <span 
                                key={index} 
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                              >
                                {offerType.toUpperCase()}
                              </span>
                            ))}
                            {brand.supportedOfferTypes.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                +{brand.supportedOfferTypes.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Incentives */}
                      {(brand.salespersonBasicIncentive > 0 || brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                        <div className="border-t border-gray-200 pt-3">
                          <div className="flex items-center space-x-4 text-sm">
                            {brand.salespersonBasicIncentive > 0 && (
                              <div className="flex items-center space-x-1">
                                <FaUsers className="text-blue-600" size={12} />
                                <span className="text-gray-600">Sales:</span>
                                <span className="font-medium text-blue-600">{brand.salespersonBasicIncentive}%</span>
                              </div>
                            )}
                            {(brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                              <div className="flex items-center space-x-1">
                                <FaPercentage className="text-purple-600" size={12} />
                                <span className="text-gray-600">Reference:</span>
                                <span className="font-medium text-purple-600">
                                  {brand.referenceBasicIncentive || brand.architectInteriorDesignerBasicIncentive || brand.architectInteriorBasicIncentive}%
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {brands.map((brand) => (
                    <div key={brand._id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {/* Brand Image */}
                          <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                            <img 
                              src={`${process.env.REACT_APP_API_BASE_URL}/brands/image/${brand._id}?t=${Date.now()}`} 
                              alt={brand.name}
                              className="w-full h-full object-cover rounded-lg"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div style={{display: 'none'}}>
                              <FaImage className="text-gray-400" size={24} />
                            </div>
                          </div>
                          
                          {/* Brand Info */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-semibold text-gray-900">{brand.name}</h3>
                              <span className="bg-blue-600 text-white px-2 py-1 rounded-md text-xs font-medium">
                                {brand.id || brand._id.toString().substring(0, 8)}
                              </span>
                            </div>
                            
                            <div className="mt-1 flex items-center space-x-4">
                              {/* Categories */}
                              {brand.categories && brand.categories.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <FaTag className="text-gray-400" size={12} />
                                  <span className="text-sm text-gray-600">
                                    {brand.categories.slice(0, 2).map(cat => {
                                      const categoryId = cat._id || cat;
                                      const categoryData = categories.find(c => c._id === categoryId);
                                      return categoryData ? categoryData.name : null;
                                    }).filter(Boolean).join(', ')}
                                    {brand.categories.length > 2 && ` +${brand.categories.length - 2} more`}
                                  </span>
                                </div>
                              )}
                              
                              {/* Offer Types */}
                              {brand.supportedOfferTypes && brand.supportedOfferTypes.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <span className="text-sm text-gray-600">
                                    {brand.supportedOfferTypes.slice(0, 2).map(type => type.toUpperCase()).join(', ')}
                                    {brand.supportedOfferTypes.length > 2 && ` +${brand.supportedOfferTypes.length - 2} more`}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Incentives */}
                            {(brand.salespersonBasicIncentive > 0 || brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                              <div className="mt-2 flex items-center space-x-4">
                                {brand.salespersonBasicIncentive > 0 && (
                                  <div className="flex items-center space-x-1">
                                    <FaUsers className="text-blue-600" size={12} />
                                    <span className="text-sm text-gray-600">Sales: {brand.salespersonBasicIncentive}%</span>
                                  </div>
                                )}
                                {(brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                                  <div className="flex items-center space-x-1">
                                    <FaPercentage className="text-purple-600" size={12} />
                                    <span className="text-sm text-gray-600">
                                      Reference: {brand.referenceBasicIncentive || brand.architectInteriorDesignerBasicIncentive || brand.architectInteriorBasicIncentive}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openModal(brand)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Brand"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteBrand(brand)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Brand"
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!loading && brands.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-12 text-center">
              <FaExclamationTriangle className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No brands found</h3>
              <p className="text-gray-500 mb-6">
                {nameFilter ? 'Try adjusting your search criteria' : 'Get started by creating your first brand'}
              </p>
              <button
                onClick={() => openModal()}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FaPlus className="mr-2" size={14} />
                Create Brand
              </button>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-700 mb-4 sm:mb-0">
                Showing <span className="font-medium">{Math.min((page - 1) * limit + 1, totalBrands)}</span> to{' '}
                <span className="font-medium">{Math.min(page * limit, totalBrands)}</span> of{' '}
                <span className="font-medium">{totalBrands}</span> results
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronLeft className="mr-1" size={12} />
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                    const showPageNum = 
                      pageNum === 1 || 
                      pageNum === totalPages || 
                      (pageNum >= page - 1 && pageNum <= page + 1);
                    
                    if (!showPageNum) {
                      if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <span key={`ellipsis-${pageNum}`} className="px-3 py-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      return null;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          pageNum === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <FaChevronRight className="ml-1" size={12} />
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Enhanced Brand Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Brand' : 'Create New Brand'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Brand Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter brand name"
                  required
                />
              </div>

              {/* Brand Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Image {!isEditing && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={!isEditing}
                    />
                  </div>
                  {imagePreview && (
                    <div className="w-20 h-20 border border-gray-300 rounded-lg overflow-hidden">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categories
                </label>
                <Select
                  isMulti
                  name="categories"
                  options={categoryOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={categoryOptions.filter(option => 
                    formData.categories.includes(option.value)
                  )}
                  onChange={handleCategoryChange}
                  placeholder="Select categories..."
                />
              </div>

              {/* Supported Offer Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supported Offer Types
                </label>
                <Select
                  isMulti
                  name="supportedOfferTypes"
                  options={OFFER_TYPE_OPTIONS}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={OFFER_TYPE_OPTIONS.filter(option => 
                    formData.supportedOfferTypes.includes(option.value)
                  )}
                  onChange={handleOfferTypesChange}
                  placeholder="Select supported offer types..."
                />
              </div>

              {/* Incentive Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salesperson Basic Incentive (%)
                  </label>
                  <input
                    type="number"
                    name="salespersonBasicIncentive"
                    value={formData.salespersonBasicIncentive}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter percentage (0-100)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Basic Incentive (%)
                  </label>
                  <input
                    type="number"
                    name="referenceBasicIncentive"
                    value={formData.referenceBasicIncentive}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter percentage (0-100)"
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" size={14} />
                      Saving...
                    </>
                  ) : (
                    isEditing ? 'Update Brand' : 'Create Brand'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandManagement;